import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { useI18n } from "@/i18n";

export default function Cookies() {
  const { lang } = useI18n();
  const isEs = lang === "es";

  const openSettings = () => {
    // Trigger CookieBanner to re-open via custom event
    window.dispatchEvent(new CustomEvent("keepsoul:cookie-settings-open"));
  };

  return (
    <div className="min-h-screen bg-obsidian text-gold-light">
      <header className="border-b border-gold-subtle bg-obsidian-2/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="cookies-back-home">
            <Logo />
          </Link>
          <Link to="/" className="text-xs text-faded hover:text-gold">
            ← Volver
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl mb-2">Política de Cookies</h1>
        <p className="text-xs text-faded mb-12">
          Última actualización: 25 de mayo de 2026 · v1.0-2026-05-25
        </p>

        {!isEs && <LegalLangNotice lang={lang} />}

        {isEs && (
          <div className="space-y-8 text-sm leading-relaxed">
            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                1. Qué son las cookies
              </h2>
              <p>
                Las cookies son pequeños archivos de texto que los sitios web
                instalan en el dispositivo del Usuario para recordar
                información sobre su visita. Esta política se ajusta a la{" "}
                <strong>
                  Guía sobre el uso de las cookies de la AEPD (Agencia Española
                  de Protección de Datos)
                </strong>{" "}
                vigente, así como al artículo 22.2 LSSI-CE y al RGPD.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                2. Cookies que utilizamos
              </h2>
              <p className="mb-3">
                KeepSoul utiliza el mínimo número de cookies necesario para
                prestar el servicio. A día de hoy:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gold-subtle">
                  <thead className="bg-obsidian-2/60 text-gold">
                    <tr>
                      <th className="border border-gold-subtle px-3 py-2 text-left">
                        Nombre
                      </th>
                      <th className="border border-gold-subtle px-3 py-2 text-left">
                        Tipo
                      </th>
                      <th className="border border-gold-subtle px-3 py-2 text-left">
                        Finalidad
                      </th>
                      <th className="border border-gold-subtle px-3 py-2 text-left">
                        Duración
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gold-subtle px-3 py-2">
                        <code>session</code>
                      </td>
                      <td className="border border-gold-subtle px-3 py-2">
                        Técnica · necesaria
                      </td>
                      <td className="border border-gold-subtle px-3 py-2">
                        Mantener tu sesión iniciada (HttpOnly + Secure +
                        SameSite). Sin ella no puedes usar la app.
                      </td>
                      <td className="border border-gold-subtle px-3 py-2">
                        30 días
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gold-subtle px-3 py-2">
                        <code>keepsoul_lang</code>
                      </td>
                      <td className="border border-gold-subtle px-3 py-2">
                        Técnica · preferencias
                      </td>
                      <td className="border border-gold-subtle px-3 py-2">
                        Recordar el idioma elegido (ES / EN / ZH).
                      </td>
                      <td className="border border-gold-subtle px-3 py-2">
                        12 meses
                      </td>
                    </tr>
                    <tr>
                      <td className="border border-gold-subtle px-3 py-2">
                        <code>keepsoul_cookie_consent</code>
                      </td>
                      <td className="border border-gold-subtle px-3 py-2">
                        Técnica · preferencias
                      </td>
                      <td className="border border-gold-subtle px-3 py-2">
                        Guardar tu elección sobre este banner de cookies.
                      </td>
                      <td className="border border-gold-subtle px-3 py-2">
                        12 meses
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-4 text-faded">
                <strong>
                  KeepSoul no utiliza actualmente cookies analíticas
                </strong>{" "}
                (Google Analytics, Posthog, Matomo, etc.) ni cookies
                publicitarias de terceros. Si en el futuro decidimos
                incorporarlas, actualizaremos esta política y solicitaremos tu
                consentimiento expreso a través del banner antes de
                instalarlas.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                3. Base legal
              </h2>
              <p>
                Las cookies técnicas necesarias se instalan sin necesidad de
                consentimiento (art. 22.2 LSSI-CE, exención técnica). Las
                cookies de preferencias se instalan con base en tu
                consentimiento, otorgado al usar el banner. Cualquier futura
                cookie analítica o publicitaria requerirá tu consentimiento
                expreso, granular, informado y revocable, conforme a la Guía
                AEPD.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                4. Gestionar tus preferencias
              </h2>
              <p className="mb-3">
                Puedes modificar o revocar tu consentimiento en cualquier
                momento:
              </p>
              <ul className="list-disc pl-6 space-y-1 mb-3">
                <li>
                  Haciendo clic en{" "}
                  <button
                    type="button"
                    onClick={openSettings}
                    className="text-gold hover:underline"
                    data-testid="cookies-open-settings"
                  >
                    Configurar mis cookies
                  </button>{" "}
                  para volver a abrir el panel de preferencias.
                </li>
                <li>
                  Borrando las cookies almacenadas desde la configuración de
                  tu navegador (
                  <a
                    href="https://support.google.com/accounts/answer/61416?hl=es"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold hover:underline"
                  >
                    Chrome
                  </a>
                  ,{" "}
                  <a
                    href="https://support.mozilla.org/es/kb/Borrar%20cookies"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold hover:underline"
                  >
                    Firefox
                  </a>
                  ,{" "}
                  <a
                    href="https://support.apple.com/es-es/guide/safari/sfri11471/mac"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold hover:underline"
                  >
                    Safari
                  </a>
                  ,{" "}
                  <a
                    href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold hover:underline"
                  >
                    Edge
                  </a>
                  ).
                </li>
                <li>
                  Si rechazas la cookie técnica de sesión, no podrás iniciar
                  sesión en KeepSoul.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl text-gold mb-3">
                5. Contacto
              </h2>
              <p>
                Para cualquier consulta sobre esta política puedes escribirnos
                a{" "}
                <a
                  href="mailto:support@keepsoul.app"
                  className="text-gold hover:underline"
                >
                  support@keepsoul.app
                </a>
                .
              </p>
            </section>
          </div>
        )}

        <div className="mt-12 pt-6 border-t border-gold-subtle text-xs text-faded text-center space-x-3">
          <Link to="/legal/aviso-legal" className="hover:text-gold">
            Aviso Legal
          </Link>
          <span className="text-faded/40">·</span>
          <Link to="/legal/privacidad" className="hover:text-gold">
            Privacidad
          </Link>
          <span className="text-faded/40">·</span>
          <Link to="/legal/terminos" className="hover:text-gold">
            Términos
          </Link>
          <span className="text-faded/40">·</span>
          <Link to="/" className="hover:text-gold">
            Inicio
          </Link>
        </div>
      </main>
    </div>
  );
}

function LegalLangNotice({ lang }) {
  if (lang === "zh") {
    return (
      <div className="rounded border border-gold-subtle bg-obsidian-2/40 p-5 text-sm leading-relaxed">
        <p className="mb-2 text-gold-light">
          法律文件目前以西班牙语提供,西班牙语是依据西班牙消费者法律管辖本合同的语言。
        </p>
        <p className="text-faded">
          英文和中文译本即将提供。如需说明,请联系:{" "}
          <a
            href="mailto:support@keepsoul.app"
            className="text-gold hover:underline"
          >
            support@keepsoul.app
          </a>
        </p>
      </div>
    );
  }
  return (
    <div className="rounded border border-gold-subtle bg-obsidian-2/40 p-5 text-sm leading-relaxed">
      <p className="mb-2 text-gold-light">
        The legal documentation is currently available in Spanish, which is the
        language governing the contract under Spanish consumer law.
      </p>
      <p className="text-faded">
        An English translation will be available soon. For clarifications:{" "}
        <a
          href="mailto:support@keepsoul.app"
          className="text-gold hover:underline"
        >
          support@keepsoul.app
        </a>
      </p>
    </div>
  );
}
